/* eslint-disable react/jsx-no-target-blank */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';
import { Typography, TextField, Box, Divider } from '@material-ui/core';
import GpsNotFixed from '@material-ui/icons/GpsNotFixed';
import { useFeature } from 'use-feature';
import { useOAT } from 'common/providers/oatProvider';
import { useContent } from 'content/content-provider';
import { AlertBanner, Splash } from 'common/components';
import { ControlledMessageModal, CloseReason } from 'common/components/MessageModal';
import CookieConsent from 'common/icons/CookieConsent';
import { useRestaurants } from 'restaurants/providers/restaurantsProvider';
import useConfig from 'common/providers/configProvider/use-config';
import { QRCodeIcon } from 'common/icons';
import { LabelButton } from 'common/components/Buttons/LabelButton';
import { QrCodeReaderModal } from 'common/components/QrCodeReaderModal';
import RestaurantSelect from '../RestaurantSelect';
import { PrivacyPolicy } from '../PrivacyPolicy';
import useStyles from './Home.styles';

const getGeolocation = () =>
  new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(({ coords }) => resolve(coords));
  });

interface ErrorListProps {
  tableNotFound: string;
  restaurantNotFound: string;
  unableToJoinTable: string;
}

interface HomeProps {
  errorList: ErrorListProps;
  queryRestaurantId: string;
  queryTableNumber?: string;
  queryCheckNumber?: string;
  loading: boolean;
}

const Home = ({
  errorList,
  queryRestaurantId,
  queryTableNumber,
  queryCheckNumber,
  loading,
}: HomeProps) => {
  const classes = useStyles();
  const [content] = useContent();
  const [isLoading, setIsLoading] = useState(loading);
  const { error, queryTableStatus, joinTable } = useOAT();
  const {
    paymentMode,
    features: { enableQRScanner, autoOpenTable },
  } = useConfig();

  const useAutoOpenTable = useFeature({
    enabled: autoOpenTable,
    name: 'autoOpenTable',
  });

  useEffect(() => {
    if (queryTableNumber && !queryCheckNumber) {
      if (useAutoOpenTable) {
        joinTable(queryRestaurantId, paymentMode, queryTableNumber);
      } else {
        queryTableStatus(
          queryRestaurantId,
          !!paymentMode,
          new URLSearchParams({ restaurantId: queryRestaurantId, tableNumber: queryTableNumber }),
          true
        );
      }
    }
    if (queryCheckNumber) {
      queryTableStatus(
        queryRestaurantId,
        !!paymentMode,
        new URLSearchParams({ restaurantId: queryRestaurantId, checkNumber: queryCheckNumber }),
        true
      );
    }
  }, [
    joinTable,
    queryTableStatus,
    paymentMode,
    useAutoOpenTable,
    queryRestaurantId,
    queryTableNumber,
    queryCheckNumber,
  ]);

  const {
    selectedRestaurant,
    tableNumber,
    resultLength,
    dispatch: { setLocation, setTableNumber },
  } = useRestaurants();

  const [cookies, setCookie] = useCookies(['cookieConsent']);
  const [restaurantCookie, setRestaurantCookie, removeRestaurantCookie] = useCookies([
    'restaurantCookie',
  ]);
  const [idCookie, setIdCookie, removeIdCookie] = useCookies(['idCookie']);
  const [openCookies, setOpenCookies] = useState(true);
  const [openQrModal, setOpenQrModal] = useState(false);
  const [showUseCurrentLocation, setShowUseCurrentLocation] = useState(true);

  const timeout = useRef<NodeJS.Timeout>();

  const irelandSpeedyUrl = useMemo(
    () => (paymentMode ? 'https://payment.speedy.zizzi.ie' : 'https://speedy.zizzi.ie/'),
    [paymentMode]
  );

  const closeModal = useCallback(
    (_: React.MouseEvent<HTMLButtonElement>, reason?: CloseReason): void => {
      if (reason === 'backdropClick') {
        return;
      }
      setOpenCookies(false);
      setCookie('cookieConsent', true, { path: '/', maxAge: 31536000 });
    },
    [setCookie]
  );

  const handleGeoLocation = useCallback(async () => {
    const location = await getGeolocation();
    setLocation(location);
  }, [setLocation]);

  useEffect(() => {
    if (error) {
      setIsLoading(false);
    }
  }, [error]);

  useEffect(() => {
    if (paymentMode && selectedRestaurant && tableNumber) {
      setShowUseCurrentLocation(false);
    }
  }, [selectedRestaurant, tableNumber, paymentMode]);

  if (paymentMode && selectedRestaurant?.shortName) {
    setRestaurantCookie('restaurantCookie', selectedRestaurant.shortName, { path: '/' });
    setIdCookie('idCookie', selectedRestaurant.restaurantId, { path: '/' });
  }

  const storedSelectedRestaurantId = useMemo(() => idCookie.idCookie, [idCookie]);

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
      const urlSearchParams = new URLSearchParams({
        tableNumber,
      });
      const restaurantId = storedSelectedRestaurantId || selectedRestaurant.restaurantId;
      timeout.current = setTimeout(() => {
        queryTableStatus(restaurantId, !!paymentMode, urlSearchParams, false);
      }, 1000);
    },
    [
      queryTableStatus,
      selectedRestaurant?.restaurantId,
      storedSelectedRestaurantId,
      tableNumber,
      paymentMode,
    ]
  );

  const handleQrModal = useCallback(() => setOpenQrModal(!openQrModal), [openQrModal]);

  if (isLoading) {
    return <Splash overrideLayoutPadding />;
  }

  return (
    <>
      <form onSubmit={handleSubmit}>
        <div>
          <Typography variant="h1">{content.newTitle}</Typography>
          {paymentMode && enableQRScanner ? (
            <>
              <Typography
                variant="h4"
                component="h2"
                className={`${classes.subtitle} ${classes.scanButton}`}
              >
                {content.qrReader.subtitle}
              </Typography>
              <LabelButton
                icon={<QRCodeIcon />}
                onClick={handleQrModal}
                label={content.qrReader.scanButton}
              />
              {openQrModal && (
                <QrCodeReaderModal open={openQrModal} closeQrScanner={handleQrModal} />
              )}
            </>
          ) : (
            <>
              <Typography variant="h4" component="h2" className={classes.subtitle}>
                {content.subtitle}
              </Typography>
              {showUseCurrentLocation && (
                <LabelButton
                  icon={<GpsNotFixed />}
                  data-testid="geolocation"
                  onClick={handleGeoLocation}
                  label={content.useLocationBtn}
                />
              )}
            </>
          )}
          {error && <AlertBanner className={classes.error}>{errorList.tableNotFound}</AlertBanner>}
          {resultLength === 0 && !selectedRestaurant && (
            <AlertBanner className={classes.noLocationBanner} data-testid="no-geolocation">
              {errorList.restaurantNotFound}
            </AlertBanner>
          )}
          {showUseCurrentLocation && (
            <Box className={classes.dividerWrapper}>
              <Divider />
              <Typography variant="body1">{content.dividerText}</Typography>
              <Divider />
            </Box>
          )}
          <RestaurantSelect
            restaurantCookie={restaurantCookie.restaurantCookie}
            removeRestaurantCookie={removeRestaurantCookie}
            removeIdCookie={removeIdCookie}
          />
          {(selectedRestaurant || !!storedSelectedRestaurantId) && (
            <Box mt={6.5} mb={10}>
              <TextField
                id="table-number"
                data-cy="table-number-select"
                label={content.tableNumber.label}
                onChange={setTableNumber}
                fullWidth
                type="number"
                inputProps={{
                  inputMode: 'numeric',
                  min: 1,
                }}
                helperText={content.howToFind}
              />
            </Box>
          )}
        </div>
        {(selectedRestaurant || !!storedSelectedRestaurantId) && tableNumber && (
          <Box flexShrink={0} className={classes.continueButton}>
            <LabelButton
              type="submit"
              label={content.submitButton}
              aria-label={content.submitButton}
            />
          </Box>
        )}
        {content.irelandRedirectMessage && (
          <div className={classes.irelandRedirect}>
            <Typography variant="subtitle2" align="center" paragraph>
              {content.irelandRedirectMessage}
              <a className={classes.linkText} href={irelandSpeedyUrl}>
                Click here.
              </a>
            </Typography>
          </div>
        )}
        <div className={classes.privacyPolicy}>
          <PrivacyPolicy />
        </div>
      </form>
      {!cookies.cookieConsent && !paymentMode && (
        <ControlledMessageModal
          open={openCookies}
          onClose={closeModal}
          icon={<CookieConsent className={classes.icon} />}
          messageCopy={content.cookieConsentModal}
          link={content.link}
        />
      )}
    </>
  );
};

export default Home;
